import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '../Icon/Icon';
import { AFFILIATE_FORM, CASINO_SHEET, MOBILE } from '../../theme/tokens';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import {
  Backdrop,
  ViewportScrim,
  Surface,
  DragHandle,
  Header,
  Title,
  CloseBtn,
  ScrollArea,
  Body,
  Footer,
} from './Sheet.styles';
import { keepTabIn, useDialogFocus } from './useDialogFocus';

/**
 * The open-sheet stack, so a dialog can tell whether it is the one being used or is
 * sitting behind another. Module scope on purpose: sheets portal to <body> from
 * anywhere in the tree, so there is no common ancestor to hold this.
 *
 * Entries stay until the dialog UNMOUNTS, and carry whether it has begun leaving, because
 * the two questions the stack answers part company during an exit:
 *
 *  - what a sheet LOOKS like: the one behind comes back up while the top one is still
 *    sliding away, so the two movements read as one rather than as a jump after the fact;
 *  - what a sheet RESPONDS to: the one behind must NOT answer Escape until the top one is
 *    really gone. Dropping the entry at close-start made the parent topmost for the 384ms
 *    of the exit, so a second Escape in that window closed it too, which is the very input
 *    that produced the stranded-dialog bug.
 */
type StackEntry = { id: symbol; closing: boolean };

const stack: StackEntry[] = [];
const stackSubscribers = new Set<() => void>();

const publishStack = (): void => {
  for (const notify of stackSubscribers) notify();
};

const indexOfEntry = (id: symbol): number => stack.findIndex((entry) => entry.id === id);

const enterStack = (id: symbol): void => {
  if (indexOfEntry(id) !== -1) return;
  stack.push({ id, closing: false });
  publishStack();
};

/** Mark a dialog as leaving: it keeps its slot for input, but stops covering the one below. */
const markClosing = (id: symbol): void => {
  const at = indexOfEntry(id);
  if (at === -1 || stack[at].closing) return;
  stack[at] = { id, closing: true };
  publishStack();
};

const leaveStack = (id: symbol): void => {
  const at = indexOfEntry(id);
  if (at === -1) return;
  stack.splice(at, 1);
  publishStack();
};

/**
 * Background scroll lock, counted rather than saved per dialog.
 *
 * Each sheet used to remember the value it found and write it back on unmount, which is
 * only correct if they unmount in the order they opened. Stacking is a supported pattern
 * now, and a route change tears a whole stack down in ONE commit: React runs sibling
 * cleanups front to back, so the outer dialog restored '' and the inner one then restored
 * the 'hidden' it had captured from the outer, leaving the page locked with nothing open.
 * A depth counter has no ordering to get wrong: the first dialog locks, the last unlocks.
 */
let scrollLockDepth = 0;
let scrollLockPrevious = '';

const lockBodyScroll = (): void => {
  if (scrollLockDepth === 0) {
    scrollLockPrevious = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLockDepth += 1;
};

const unlockBodyScroll = (): void => {
  scrollLockDepth = Math.max(0, scrollLockDepth - 1);
  if (scrollLockDepth === 0) document.body.style.overflow = scrollLockPrevious;
};

const subscribeStack = (notify: () => void): (() => void) => {
  stackSubscribers.add(notify);
  return () => {
    stackSubscribers.delete(notify);
  };
};

/** True when ANY dialog sits above this one, including one on its way out: it owns the keyboard. */
const isBehind = (id: symbol): boolean => {
  const at = indexOfEntry(id);
  return at !== -1 && at < stack.length - 1;
};

/** True when a dialog that is still ARRIVING or resting covers this one: it should recede. */
const isCovered = (id: symbol): boolean => {
  const at = indexOfEntry(id);
  if (at === -1) return false;
  return stack.slice(at + 1).some((entry) => !entry.closing);
};

/**
 * Dismiss the surrounding sheet through its own exit choreography.
 *
 * Any control wired straight to the `onClose` the caller owns unmounts the dialog on the
 * spot — no slide, it simply blinks out — because only the sheet knows how it leaves.
 * Pass the returned `closeHandle` to {@link Sheet} and call `dismiss` from footers,
 * cancel buttons and finished actions instead. `dismiss` falls back to `onClose` when no
 * sheet has claimed the handle yet, so it is always safe to call.
 */
export const useSheetDismiss = (
  onClose: () => void,
): { closeHandle: React.MutableRefObject<(() => void) | null>; dismiss: () => void } => {
  const closeHandle = React.useRef<(() => void) | null>(null);
  const dismiss = React.useCallback(() => {
    (closeHandle.current ?? onClose)();
  }, [onClose]);
  return { closeHandle, dismiss };
};

export type SheetProps = {
  /** Dialog heading (i18n key). Optional when a custom {@link header} is supplied. */
  title?: string;
  /** Interpolation values for the title. */
  titlePlaceholders?: Record<string, string | number>;
  /**
   * Custom header content, rendered in place of the default text {@link title}
   * (e.g. a feature icon + name + price). The Sheet still owns the close button
   * and the header divider; provide an {@link ariaLabel} so the dialog stays labelled.
   */
  header?: React.ReactNode;
  /** Accessible label for the dialog (i18n key) — defaults to the title. */
  ariaLabel?: string;
  ariaLabelPlaceholders?: Record<string, string | number>;
  onClose: () => void;
  /** Pinned action row at the bottom of the sheet (e.g. a CTA). */
  footer?: React.ReactNode;
  /**
   * Max width of the desktop sheet as a CSS length (mobile keeps its full-bleed
   * bottom sheet). Defaults to `560px`, the studio's standard dialog; pass `100%`
   * to let a wide modal fill the space the backdrop gives it — on desktop that is
   * the content card, not the viewport, because the backdrop is re-pinned to the
   * card (see `contentAreaOverlay`), less its 24px gutter. The blog preview does
   * this: it renders a whole article page, which a 560px column cannot carry.
   *
   * Always clamped to the backdrop, so an over-generous value can never overflow it.
   */
  maxWidth?: string;
  /**
   * Max height of the desktop sheet as a CSS length (mobile keeps its bottom-sheet
   * cap). Defaults to `min(720px, 100%)`; pass `100%` to let a tall modal grow to
   * the full available viewport height. The sheet stays content-sized either way:
   * it only gets as tall as its content needs, up to this cap.
   */
  maxHeight?: string;
  /**
   * Filled with the sheet's own animated close, for content that dismisses the dialog
   * itself (a Cancel button, a menu item, a finished action).
   *
   * Anything wired straight to `onClose` unmounts the dialog on the spot — no slide, it
   * simply blinks out — because only the sheet knows how it leaves. Callers keep a ref,
   * pass it here, and call `handle.current?.()` instead.
   */
  closeHandle?: React.MutableRefObject<(() => void) | null>;
  /**
   * The dialog is in the middle of something that must not be interrupted: a card confirm,
   * an upload. While true it refuses every dismissal (Escape, backdrop, the phone drag, the
   * close button, {@link closeHandle}) and the close button reads as disabled.
   *
   * Say it here rather than by vetoing {@link onClose} from the caller. A veto arrives after
   * the sheet has already committed to leaving, and `closing` is one-way, so it strands the
   * dialog: mounted and off screen, with a full-viewport backdrop still swallowing clicks and
   * the page scroll still locked, unrecoverable without a reload.
   */
  busy?: boolean;
  /**
   * Lay an extra wash over the app behind the modal, faded in. The replace-logo sheet
   * turns this on while a file is dragged over the page, so everything except the drop
   * target recedes — not just the modal's own content.
   */
  deepenBackdrop?: boolean;
  /**
   * Smoothly animate the sheet's height when its content changes size (a tab
   * switch, a section or footer appearing, …) instead of snapping to the new
   * height. Off by default; the sheet stays content-sized (and capped by
   * {@link maxHeight}) either way.
   */
  animateResize?: boolean;
  children: React.ReactNode;
};

/**
 * Shared modal / bottom-sheet shell for the Casino Settings page — a centred modal
 * on desktop, a bottom sheet on mobile (portalled to `<body>` so it dims the whole
 * viewport, above all layout chrome). Owns the backdrop-click / Escape close and
 * the background scroll-lock; the caller supplies the title, body and optional
 * pinned footer. Reuses the affiliate-form sheet tokens + choreography.
 */
export const Sheet = ({
  title,
  titlePlaceholders,
  header,
  ariaLabel,
  ariaLabelPlaceholders,
  onClose,
  footer,
  maxWidth,
  maxHeight,
  animateResize,
  deepenBackdrop = false,
  closeHandle,
  busy = false,
  children,
}: SheetProps): React.ReactElement => {
  const { t } = useTranslation();

  // Play the exit animation, THEN actually close (unmount). A guard prevents a
  // double-trigger; the timeout matches the sheet's exit duration.
  const [closing, setClosing] = React.useState(false);
  const closeTimer = React.useRef<number | null>(null);
  // Read through a ref so the refusal is current for listeners bound once (Escape, the drag
  // gesture) without re-subscribing them every time the caller's `busy` flips.
  const busyRef = React.useRef(busy);
  busyRef.current = busy;
  const requestClose = React.useCallback(() => {
    // The ONE place a dismissal can be refused. Every route in (Escape, backdrop, the drag
    // gesture, the close button, a caller's own closeHandle) comes through here, so a caller
    // never has to veto `onClose` from outside: by then the exit has already started and the
    // dialog would be stranded, mounted and invisible, with its backdrop eating every click.
    if (busyRef.current) return;
    setClosing((already) => {
      if (already) return already;
      closeTimer.current = window.setTimeout(onClose, CASINO_SHEET.CLOSE_MS);
      return true;
    });
  }, [onClose]);
  React.useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  // --- Drag to dismiss (phone only) ----------------------------------------
  // The sheet follows the finger down and is let go past a threshold, or on a flick that
  // doesn't get that far; anything short of that settles back. Bound to the grab handle
  // rather than the whole sheet, so it can never fight the body's own scrolling.
  //
  // Native listeners on the handle, not React's synthetic ones: the gesture takes pointer
  // capture, and captured `pointermove`s do not reach React's delegated handlers — the
  // sheet would then only learn about the drag when the finger lifted.
  const isSheet = useMediaQuery(MOBILE);
  const handleRef = React.useRef<HTMLDivElement>(null);
  const [dragY, setDragY] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  // Sticky for the sheet's whole life, and NOT the same thing as `dragging`. The entrance
  // keyframe is suppressed while the sheet is being dragged; letting it come back when the
  // finger lifts would set `animation-name` from `none` to `slideIn` again, which per spec
  // starts a NEW animation, so a sheet released under the threshold would replay its entire
  // entrance instead of easing back over SETTLE_MS. Once dragged, the transform stays the
  // sheet's own business until it closes.
  const [dragged, setDragged] = React.useState(false);
  // The gesture's origin lives in a REF, not in the effect's closure, and the effect
  // depends only on the breakpoint. Anything else re-subscribes the listeners on an
  // unrelated re-render (an inline `onClose` is enough), which wipes the origin and
  // leaves the sheet ignoring the rest of the drag.
  const dragFrom = React.useRef<{ y: number; at: number } | null>(null);
  const closeRef = React.useRef(requestClose);
  closeRef.current = requestClose;

  React.useEffect(() => {
    const handle = handleRef.current;
    if (!handle || !isSheet) return undefined;

    const onDown = (event: PointerEvent): void => {
      // Primary pointer only: a second finger (or a right-click) landing on the handle
      // mid-gesture would otherwise reset the origin and the sheet would jump.
      if (!event.isPrimary || event.button !== 0) return;
      // Don't even follow the finger while the dialog is busy: a sheet that tracks the drag
      // and then refuses to leave reads as broken rather than as held.
      if (busyRef.current) return;
      handle.setPointerCapture(event.pointerId);
      dragFrom.current = { y: event.clientY, at: performance.now() };
      setDragging(true);
      setDragged(true);
    };
    const onMove = (event: PointerEvent): void => {
      const from = dragFrom.current;
      if (!from) return;
      // Downward only: dragging up would lift the sheet off its own bottom edge.
      setDragY(Math.max(0, event.clientY - from.y));
    };
    const onUp = (event: PointerEvent): void => {
      const start = dragFrom.current;
      dragFrom.current = null;
      setDragging(false);
      if (!start) return;
      const travelled = Math.max(0, event.clientY - start.y);
      const velocity = travelled / Math.max(1, performance.now() - start.at);
      // A flick still has to be a real gesture: a fast 10px twitch is not a dismissal.
      const flicked = velocity > CASINO_SHEET.DISMISS_VELOCITY && travelled > CASINO_SHEET.FLICK_MIN_PX;
      if (travelled > CASINO_SHEET.DISMISS_PX || flicked) {
        closeRef.current();
        return;
      }
      setDragY(0);
    };

    handle.addEventListener('pointerdown', onDown);
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
    return () => {
      handle.removeEventListener('pointerdown', onDown);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      // Crossing the breakpoint mid-gesture (a phone rotated into landscape) tears these
      // listeners down, so the `pointerup` that would have settled the sheet never arrives.
      // Put it back at rest here, or it sits wherever the finger left it with its
      // transition disabled and no way back.
      dragFrom.current = null;
      setDragging(false);
      setDragY(0);
    };
  }, [isSheet]);

  // Where this dialog sits in the stack: registered on mount, marked as leaving the moment it
  // starts closing, and surrendered only on unmount (see the registry above).
  //
  // Stable per instance and read out as a plain value, so it can be minted once instead of on
  // every render: this dialog's identity is what the Escape handler and the stack effects
  // below actually depend on.
  const [stackId] = React.useState(() => Symbol('sheet'));
  // `covered`, not `behind`: this drives how the sheet LOOKS, so it has to let go as soon as
  // the dialog above starts leaving, while the keyboard stays with that dialog until it is
  // gone. Returns a primitive, so a fresh closure each render is fine.
  const behind = React.useSyncExternalStore(
    subscribeStack,
    () => isCovered(stackId),
    () => false,
  );
  React.useEffect(() => {
    enterStack(stackId);
    return () => leaveStack(stackId);
  }, [stackId]);
  React.useEffect(() => {
    if (closing) markClosing(stackId);
  }, [closing, stackId]);

  React.useEffect(() => {
    if (!closeHandle) return undefined;
    closeHandle.current = requestClose;
    return () => {
      closeHandle.current = null;
    };
  }, [closeHandle, requestClose]);

  const onBackdropClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (event.target === event.currentTarget) {
        requestClose();
      }
    },
    [requestClose],
  );

  // Lock background scroll + close on Escape while the sheet is mounted.
  //
  // Only the dialog on TOP answers Escape. Every mounted sheet listens on `document`, so
  // an ungated handler runs the whole stack's exit at once: backing out of a nested dialog
  // would also discard the surface underneath it. Worse, a caller that vetoes the parent's
  // `onClose` while a child is open cannot undo the exit that already started (`closing` is
  // a one-way latch), which strands the parent mounted and invisible, its backdrop still
  // swallowing every click and the scroll lock still on.
  //
  // Runs ONCE per dialog (`stackId` is stable), never on `requestClose`. That identity changes
  // whenever the caller's `onClose` does, which for an inline arrow is every render, and each
  // re-run would take the scroll lock again. The current close is read through the ref instead.
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (isBehind(stackId)) return;
      closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', onKey);
      unlockBodyScroll();
    };
  }, [stackId]);

  // ----- optional smooth height animation (`animateResize`) -----------------
  // We drive the sheet's height from its measured content (header + body + footer)
  // and let the CSS `transition: height` animate it. The CSS `max-height` still
  // caps the rendered height, so when content exceeds the cap the ScrollArea
  // scrolls and the measured value is simply clamped (no visual jump); we never
  // compute the cap here. The transition is enabled only AFTER the first measure
  // lands, so opening (scaleIn) isn't accompanied by a height grow-in.
  const sheetRef = React.useRef<HTMLDivElement>(null);

  // The keyboard's way through: the focus moves into the sheet as it opens, Tab goes round its controls while it is the
  // dialog on top (the page behind is inert, as aria-modal says), and the focus goes back to its opener as it closes.
  useDialogFocus(sheetRef);
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab' || isBehind(stackId)) return;
      keepTabIn(event, sheetRef.current);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [stackId]);

  const headerRef = React.useRef<HTMLDivElement>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const footerRef = React.useRef<HTMLDivElement>(null);
  const [resizeReady, setResizeReady] = React.useState(false);

  const measureHeight = React.useCallback(() => {
    const sheet = sheetRef.current;
    const body = bodyRef.current;
    if (!sheet || !body) return;
    // Every child the sheet lays out, or the explicit height comes up short and the deficit
    // is taken out of the ScrollArea (the only flexible one). The grab handle is a real row
    // on phones and collapses to 0 on desktop, so measuring it costs nothing there.
    const handleH = handleRef.current?.offsetHeight ?? 0;
    const headerH = headerRef.current?.offsetHeight ?? 0;
    const footerH = footerRef.current?.offsetHeight ?? 0;
    sheet.style.height = `${handleH + headerH + body.offsetHeight + footerH}px`;
  }, []);

  React.useLayoutEffect(() => {
    if (!animateResize) return;
    measureHeight(); // initial size, set before the transition is enabled
    // Re-measure whenever the body's content box changes height (tab swap, a row
    // appearing, async content settling). The footer toggles together with the
    // body in practice, so observing the body alone captures both.
    const body = bodyRef.current;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measureHeight()) : null;
    if (ro && body) ro.observe(body);
    const raf = window.requestAnimationFrame(() => setResizeReady(true));
    return () => {
      if (ro) ro.disconnect();
      window.cancelAnimationFrame(raf);
    };
  }, [animateResize, measureHeight]);

  return createPortal(
    <>
      <ViewportScrim $on={deepenBackdrop} aria-hidden="true" />
      <Backdrop onClick={onBackdropClick} $closing={closing}>
        <Surface
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          $closing={closing}
          $dragY={dragY}
          $dragging={dragging}
          $dragged={dragged}
          $behind={behind}
          $maxWidth={maxWidth}
          $maxHeight={maxHeight}
          $animateResize={animateResize && resizeReady}
          aria-label={t(ariaLabel ?? title ?? '', ariaLabelPlaceholders ?? titlePlaceholders)}
        >
          <DragHandle ref={handleRef} aria-hidden="true" />
          <Header ref={headerRef} $dim={deepenBackdrop}>
            {header ?? <Title text={title ?? ''} placeholders={titlePlaceholders} tag="span" />}
            <CloseBtn type="button" aria-label={t('Close')} onClick={requestClose} disabled={busy}>
              <Icon name="close" size={18} color={AFFILIATE_FORM.closeGlyph} />
            </CloseBtn>
          </Header>
          <ScrollArea>
            <Body ref={bodyRef}>{children}</Body>
          </ScrollArea>
          {footer ? (
            <Footer ref={footerRef} $dim={deepenBackdrop}>
              {footer}
            </Footer>
          ) : null}
        </Surface>
      </Backdrop>
    </>,
    document.body,
  );
};

export default Sheet;
